import React, { useContext, useState } from 'react';
import * as Yup from 'yup';
import AuthContext from 'AppContext/AuthProvider';

import { Formik } from 'formik';
import Button from 'components/Button';
import { AUTH_ERRORS } from 'Constants/ErrorMessages';
import AuthModel, { TwoFactorFormData } from 'models/auth';
import { IS_USER_AUTHENTICATED, ROUTES } from 'Constants/variables';
import { useRouter } from 'next/router';
import utils from 'utils';
import { sendEvent } from 'hooks/useAnalytics';
import styles from './twoFactor.module.sass';

const ValidationSchema = Yup.object().shape({
  code: Yup.string()
    .required(AUTH_ERRORS.CODE_REQUIRED)
    .matches(/^\d{6}$/, AUTH_ERRORS.CODE_INVALID),
});

const TwoFactor: React.FC = () => {
  const { twoFactor, setSSOLoggedIn } = useContext(AuthContext);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | undefined>('');
  const [isResending, setIsResending] = useState(false);
  const router = useRouter();

  const handleSubmit = async () => {
    if (!twoFactor?.email) return;
    try {
      let loginType = 'email';
      const response = await AuthModel.twoFactorSignIn({
        email: twoFactor.email,
        code,
      });
      if (response.code !== 200) {
        setError(response.message);
        return;
      }
      if (twoFactor.sso) {
        loginType = 'sso';
        setSSOLoggedIn('true');
      }
      sendEvent('login', { login_type: loginType, nonInteraction: true });

      utils.setCookie('isUserAuthenticated', IS_USER_AUTHENTICATED.YES);
      router.push(ROUTES.DASHBOARD);
    } catch (error: any) {
      setError(error?.response?.data?.error || error?.message || AUTH_ERRORS.GENERIC_ERROR_500);
    }
  };

  const handleResend = async () => {
    if (!twoFactor?.email) return;
    try {
      setIsResending(true);
      await AuthModel.reSendCode({
        email: twoFactor.email,
      });
    } catch (error: any) {
      setError(error?.response?.data?.error || error?.message || AUTH_ERRORS.GENERIC_ERROR_500);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className={styles.twoFactorContainer}>
      <h1>Verify with Email Authentication</h1>
      {twoFactor?.isLocked ? (
        <p>There seems to be a problem with your verification. Please contact KnoPro Team.</p>
      ) : (
        <div className={styles.formContainer}>
          <p>
            Verification code was sent to <strong>{twoFactor?.email}</strong>
            <br />
            Check your email and enter the code below:
          </p>
          <Formik<Partial<TwoFactorFormData>>
            initialValues={{ code: '' }}
            validationSchema={ValidationSchema}
            onSubmit={handleSubmit}
          >
            {formikProps => (
              <form onSubmit={formikProps.handleSubmit}>
                <label htmlFor="code">
                  Verification code:
                  <input
                    type="text"
                    name="code"
                    value={code}
                    onChange={e => {
                      if (error) setError(undefined);
                      formikProps.setFieldError('code', '');

                      if (/^\d{0,6}$/.test(e.target.value)) {
                        setCode(e.target.value);
                        formikProps.setFieldValue('code', e.target.value);
                      }
                    }}
                    maxLength={6}
                    pattern="[0-9]{6}"
                    required
                  />
                  {(error || formikProps.errors.code) && (
                    <p className={styles.error}>
                      {[error, formikProps.errors.code].filter(Boolean).join(', ')}
                    </p>
                  )}
                </label>
                <Button type="submit" theme="Filled - Lime Green Gradient">
                  Verify
                </Button>
              </form>
            )}
          </Formik>
          <p>
            Haven&apos;t received the email?
            <button
              type="button"
              className={styles.resendButton}
              disabled={isResending}
              onClick={handleResend}
            >
              Send it again
            </button>
          </p>
        </div>
      )}
    </div>
  );
};

export default TwoFactor;
